/**
 * AnomalyBanner — alerta de variação atípica de custo (SP3).
 *
 * Estratégia dupla (D6 do plano):
 *  1. Prop `anomalyTenants`: tenants com anomaly=true vindos de PortfolioResponse.tenants
 *     (disponível sem custo extra — filtragem no caller).
 *  2. Hook react-query consumindo GET /finops/anomalies (SP2) para anomalias CE
 *     não incluídas no portfólio (ex.: contas sem budget cadastrado).
 *
 * Renderiza um Alert Mantine color="yellow" se houver ao menos 1 anomalia.
 * Falha-segura: erro de rede / loading → banner não aparece.
 * Retorna null quando não há anomalias (não ocupa espaço).
 *
 * Cores apenas via tokens DS/Mantine.
 */
import { Alert, Badge, Group, Stack, Text } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { IconAlertTriangle } from '@tabler/icons-react'

import { getAnomalies } from '@/api/finops'
import type { FinopsAnomalyItem } from '@/api/finops'
import type { PortfolioTenantRow } from '@/api/finopsDashboard'

// ─── tipos ──────────────────────────────────────────────────────────────────

export interface AnomalyBannerProps {
  /**
   * Tenants com `anomaly=true` filtrados de PortfolioResponse.tenants pelo caller.
   * Usado como fonte primária instantânea (sem fetch extra).
   */
  anomalyTenants?: PortfolioTenantRow[]
  /**
   * Quando true, também consulta GET /finops/anomalies (SP2) para complementar.
   * Default: true. Desabilite em contextos onde a chamada já ocorreu.
   */
  fetchEndpoint?: boolean
}

// ─── helpers ────────────────────────────────────────────────────────────────

/** Extrai identificador legível de uma anomalia CE. */
function anomalyLabel(item: FinopsAnomalyItem): string {
  const account = item.aws_account_id ? ` (${item.aws_account_id})` : ''
  return `${item.event_name}${account}`
}

// ─── componente ─────────────────────────────────────────────────────────────

export function AnomalyBanner({
  anomalyTenants = [],
  fetchEndpoint = true,
}: AnomalyBannerProps) {
  const { data: anomalyData } = useQuery({
    queryKey: ['finops', 'anomalies'],
    queryFn: () => getAnomalies({ limit: 20, acknowledged: false }),
    // Falha-segura: em caso de erro, retorna lista vazia sem quebrar.
    enabled: fetchEndpoint,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 min — alinhado ao TTL do cache Redis (SP2)
  })

  const endpointItems: FinopsAnomalyItem[] = anomalyData?.items ?? []

  // Deduplicar: contas do endpoint já listadas via portfólio não aparecem duas vezes.
  const portfolioAccountIds = new Set(anomalyTenants.map((t) => t.account_id))
  const uniqueEndpointItems = endpointItems.filter(
    (it) => !it.aws_account_id || !portfolioAccountIds.has(it.aws_account_id),
  )

  const hasTenantAnomalies = anomalyTenants.length > 0
  const hasEndpointAnomalies = uniqueEndpointItems.length > 0

  // Nada a mostrar → não renderiza (não ocupa espaço).
  if (!hasTenantAnomalies && !hasEndpointAnomalies) return null

  const totalCount = anomalyTenants.length + uniqueEndpointItems.length

  return (
    <Alert
      icon={<IconAlertTriangle size={16} />}
      color="yellow"
      variant="light"
      radius="md"
      title={
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" fw={700}>
            Variação atípica detectada
          </Text>
          <Badge size="xs" color="yellow" variant="filled">
            {totalCount}
          </Badge>
        </Group>
      }
    >
      <Stack gap={6}>
        {/* Tenants do portfólio com anomaly=true */}
        {hasTenantAnomalies && (
          <div>
            <Text size="xs" c="dimmed" fw={600} mb={2}>
              Organizações
            </Text>
            <Group gap="xs" wrap="wrap">
              {anomalyTenants.map((t) => (
                <Badge key={t.tenant_id} size="xs" color="yellow" variant="outline">
                  {t.name}
                  {t.account_id ? ` · ${t.account_id}` : ''}
                </Badge>
              ))}
            </Group>
          </div>
        )}

        {/* Anomalias CE do endpoint SP2 */}
        {hasEndpointAnomalies && (
          <div>
            <Text size="xs" c="dimmed" fw={600} mb={2}>
              Eventos CE detectados
            </Text>
            <Group gap="xs" wrap="wrap">
              {uniqueEndpointItems.map((it) => (
                <Badge key={it.id} size="xs" color="yellow" variant="outline">
                  {anomalyLabel(it)}
                </Badge>
              ))}
            </Group>
          </div>
        )}

        <Text size="xs" c="dimmed" mt={2}>
          Revise as contas listadas — o consumo está fora do padrão histórico.
        </Text>
      </Stack>
    </Alert>
  )
}
